/**
 * Le starting block se prépare pendant que l'élève fait son test du matériel : la lecture de
 * l'avant-séance (`GET live-sessions/{id}/student-pre-call`), puis, s'il y a lieu, la création de la
 * conversation avec Sam. La règle est celle de l'ancien `PreCallScreen`, mot pour mot
 * (`startingBlockContextFor`).
 *
 * Une seule demande, mais pas forcément au montage : arrivé par un lien direct, l'élève a sa séance
 * — donc le drapeau de sa salle — une seconde après l'affichage. Elle part dès que `enabled` l'est.
 *
 * Un échec ne retient jamais l'élève : Sam ne vient pas, l'entrée reste ouverte, et l'échec part dans
 * Bugsnag au lieu d'être oublié.
 */
import { useEffect, useMemo, useRef, useState } from "react"

import { liveService } from "../../../data/services/liveService"
import { createTelemetry, type EngineTelemetry, type TelemetryRole } from "../../shared/telemetry"
import { defaultCheckpointApi, type CheckpointApi } from "./checkpointApi"
import { isStartingBlockContext, type ApiCheckpointConversation } from "./checkpointConversationMachine"

/** Ce que l'avant-séance dit de l'élève et de sa séance du jour. */
export type ApiStudentPreCall = {
    student?: { has_study_program?: boolean; has_checkpoint?: boolean } | null
    live_occurrence?: { chapter_id?: number | null; subject_id?: number | null } | null
    connection_duration?: number | null
}

/** Au-delà de ce temps de connexion à la séance du jour, l'élève est déjà passé : pas de démarrage. */
const ALREADY_CONNECTED_SECONDS = 5 * 60

/**
 * Le démarrage qui convient, ou `null` : avec un programme, il faut le chapitre du jour ; sans, la
 * matière ; et jamais deux fois (un checkpoint déjà fait, ou l'élève déjà connecté plus de 5 min).
 */
export function startingBlockContextFor(data: ApiStudentPreCall | null | undefined): string | null {
    const context = data?.student?.has_study_program === true ? "starting_block_study_program" : "starting_block"
    if (context === "starting_block_study_program" && !data?.live_occurrence?.chapter_id) return null
    if (context === "starting_block" && !data?.live_occurrence?.subject_id) return null
    if (data?.student?.has_checkpoint === true || (data?.connection_duration ?? 0) > ALREADY_CONNECTED_SECONDS)
        return null
    return context
}

export type UseStartingBlockOptions = {
    /** Le drapeau `checkpoint-sb` de la salle, pour un élève. */
    enabled: boolean
    liveSessionId: number | string
    studentId: number | string | null
    role: TelemetryRole
    api?: CheckpointApi & { preCall?: (liveSessionId: number | string) => Promise<ApiStudentPreCall> }
    telemetry?: EngineTelemetry
}

export type StartingBlock = {
    /** La lecture ou la création tourne : l'entrée attend, sinon un appui rapide entrerait sans Sam. */
    preparing: boolean
    /** La conversation prête, ou `null` : pas de démarrage aujourd'hui. */
    conversation: ApiCheckpointConversation | null
}

const defaultPreCall = async (liveSessionId: number | string): Promise<ApiStudentPreCall> => {
    const response = await liveService.getStudentPreCallData({ id: liveSessionId })
    return (response?.data?.data ?? response?.data ?? {}) as ApiStudentPreCall
}

export function useStartingBlock(options: UseStartingBlockOptions): StartingBlock {
    const { enabled, liveSessionId, studentId, role } = options
    const api = options.api ?? defaultCheckpointApi
    const preCall = options.api?.preCall ?? defaultPreCall
    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("starting-block", { role }),
        // La télémétrie d'une entrée montée ne change pas en route.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )
    const [result, setResult] = useState<StartingBlock>({ preparing: false, conversation: null })
    const askedRef = useRef(false)
    const mountedRef = useRef(true)
    useEffect(
        () => () => {
            mountedRef.current = false
        },
        []
    )

    useEffect(() => {
        if (!enabled || askedRef.current || studentId == null) return undefined
        askedRef.current = true
        setResult({ preparing: true, conversation: null })
        void (async () => {
            let conversation: ApiCheckpointConversation | null = null
            try {
                const context = startingBlockContextFor(await preCall(liveSessionId))
                telemetry.transition("idle", context ?? "skipped", "preCallRead")
                if (context != null) {
                    const created = await api.create({
                        student_id: studentId,
                        live_session_id: liveSessionId,
                        trigger_context: context,
                    })
                    // Seul un démarrage se montre ici : jamais une main levée ou un silence restés en erreur.
                    if (isStartingBlockContext(created?.conversation?.trigger_context ?? "")) conversation = created
                    telemetry.transition(context, conversation != null ? "ready" : "ignored", "conversationCreated")
                }
            } catch (error) {
                telemetry.error("prepare", error)
            }
            if (mountedRef.current) setResult({ preparing: false, conversation })
        })()
        return undefined
    }, [enabled, liveSessionId, studentId, api, preCall, telemetry])

    return result
}
